import Close16 from '@carbon/icons-react/es/close/16';
import Search16 from '@carbon/icons-react/es/search/16';
import { Tooltip } from 'antd';
import { ButtonType } from 'antd/lib/button';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { InputProps, InputRef } from 'antd/lib/input';
import {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import { ifProp, prop, switchProp } from 'styled-tools';
import { Button } from 'components/facade/button';
import { queueMacrotask } from 'utils/macrotask';
import { IconWrapper } from '../icon-shell/icon-shell';
import { Height, HeightStyle, InputFocus, InputStyle, Input } from './input';

type ExpandDirection = 'left' | 'right';
const SearchButton = styled(Button).attrs({
  icon: (
    <IconWrapper>
      <Search16 />
    </IconWrapper>
  ),
})`
  :root[data-new-theme-enabled='true'] & {
    &:hover,
    &:active,
    &:focus {
      border-radius: var(--border-radius-small);
    }
  }
`;
const CloseButton = styled(Button).attrs({ icon: <Close16 /> })`
  :root[data-new-theme-enabled='true'] & {
    &:hover,
    &:active,
    &:focus {
      border-radius: var(--border-radius-small);
    }
  }
`;
const Wrapper = styled.div<{ active: boolean; size: SizeType }>`
  display: flex;
  align-items: center;
  transition: all 0.11s cubic-bezier(0.2, 0, 0.38, 0.9);
  max-width: ${HeightStyle};
  overflow: hidden;

  ${ifProp(
    'active',
    css`
      ${InputStyle};
      max-width: 100%;
      padding: 0;
      &:focus-within {
        ${InputFocus};
      }

      :root[data-new-theme-enabled='true'] & {
        outline: none;
        border-radius: var(--border-radius-small);
        background-color: var(--white-100);
        border: 1px solid var(--gray-20);
        &:focus-within {
          border: 2px solid var(--gray-20);
        }
      }
      ${SearchButton} {
        pointer-events: none;
      }
    `,
  )};

  ${Input} {
    padding: 0;
    height: calc(${HeightStyle} - 6px);
    border: none;
    outline: none;
  }

  :root[data-new-theme-enabled='true'] & {
    ${Input} {
      background-color: transparent;
      color: var(--black-100);
    }
  }
`;
const AnimationWrapper = styled.div<{
  active: boolean;
  size: SizeType;
  direction: ExpandDirection;
  minWidth?: string;
  width?: string | number;
  expanded?: boolean;
}>`
  position: relative;
  min-width: ${(props) => props.minWidth ?? '180px'};
  width: ${(props) => props.width ?? '100%'};
  ${Height};
  ${Wrapper} {
    position: ${(props) => (props.expanded ? 'static' : 'absolute')};
    ${switchProp(prop('direction', 'left'), {
      left: css`
        left: 0px;
      `,
      right: css`
        right: 0px;
      `,
    })};
  }
`;

export type SearchInputProps = InputProps & {
  direction?: ExpandDirection;
  tooltip?: ReactNode;
  searchButtonProps?: {
    type: ButtonType;
    disabled?: boolean;
  };
  minWidth?: string;
  onFocusBeforeActive?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onClose?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  expanded?: boolean;

  /**
   * @desc `controlled` prop lets the consumer control the value of the input.
   *        when an input is controlled, `onChange` listener should update consumer's value state in sync.
   *
   */
  controlled?: boolean;
};

export const SearchInput = forwardRef<InputRef, SearchInputProps>(
  (
    {
      controlled,
      expanded = false,
      onChange,
      onBlur,
      onFocus,
      onFocusBeforeActive,
      onClose,
      size = 'middle',
      direction = 'left',
      className,
      tooltip,
      searchButtonProps = { type: 'default' },
      minWidth,
      width,
      ...props
    },
    ref,
  ) => {
    const inputRef = useRef<InputRef>(null);
    const [active, _setActive] = useState(expanded);
    const [value, setValue] = useState(controlled ? props.value ?? '' : '');

    const setActive = useCallback(
      (_active: boolean) => {
        if (!expanded) {
          _setActive(_active);
        }
      },
      [expanded],
    );

    const openSearchInput = useCallback(() => {
      setActive(true);
      queueMacrotask(() => {
        inputRef.current?.focus();
      });
    }, [setActive]);

    const onChangeValue = (e) => {
      setValue(e.target.value);
      onChange?.(e);
    };

    const refCallback = useCallback(
      (element: InputRef) => {
        (inputRef as React.MutableRefObject<InputRef>).current = element;

        if (ref) {
          if (typeof ref === 'function') {
            ref(element);
          } else {
            ref.current = element;
          }
        }
      },
      [ref],
    );

    useEffect(() => {
      if (controlled) {
        setValue((props.value as string) || '');
      }
    }, [props.value, controlled]);

    return (
      <AnimationWrapper
        active={active}
        expanded={expanded}
        size={size}
        direction={direction}
        className={className}
        minWidth={minWidth}
        width={width}
      >
        <Wrapper active={active} size={size} className="search-input-wrapper">
          <Tooltip title={tooltip}>
            <SearchButton
              size={size}
              type={active ? 'text' : searchButtonProps.type}
              disabled={searchButtonProps?.disabled ?? false}
              tabIndex={active ? -1 : 0}
              onFocus={openSearchInput}
              onClick={openSearchInput}
              data-cy="search-input.button"
            />
          </Tooltip>
          <Input
            {...props}
            tabIndex={active ? 0 : -1}
            ref={refCallback}
            value={value}
            onChange={onChangeValue}
            onFocus={(e) => {
              onFocusBeforeActive?.(e);
              setActive(true);
              onFocus?.(e);
            }}
            onBlur={(e) => {
              if (!e.target.value) {
                setActive(false);
              }
              onBlur?.(e);
            }}
            data-cy="search.text-field"
          />
          {!expanded || value ? (
            <CloseButton
              data-cy="search-input.close-button"
              type="text"
              size={size}
              tabIndex={-1}
              onClick={(e) => {
                setActive(false);
                const event = Object.create(e);
                const target = inputRef.current!.input;
                const currentTarget = target!.cloneNode(
                  true,
                ) as HTMLInputElement;

                event.target = currentTarget;
                event.currentTarget = currentTarget;
                currentTarget.value = '';

                onChangeValue(event as React.ChangeEvent<HTMLInputElement>);
                onClose?.(event as React.ChangeEvent<HTMLInputElement>);
              }}
            />
          ) : null}
        </Wrapper>
      </AnimationWrapper>
    );
  },
);
